const STORAGE_PREFIX = 'twine_project_publish_flash:';

function canUseStorage() {
  return typeof window !== 'undefined' && window.sessionStorage;
}

/**
 * @param {string|number} projectId
 * @returns {string}
 */
function storageKey(projectId) {
  return STORAGE_PREFIX + String(projectId);
}

/**
 * Convert a blob: object URL to a data URL so it survives a full navigation.
 * Passes through data:/http(s): URLs unchanged.
 *
 * @param {string|null|undefined} url
 * @returns {Promise<string|null>}
 */
export async function toStorablePreviewUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  if (!url.startsWith('blob:')) {
    return url;
  }

  if (typeof window === 'undefined' || typeof fetch === 'undefined') {
    return null;
  }

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
}

/**
 * Stash a one-shot post-publish flash (banner + optional media preview).
 * Survives the create → project page full navigation.
 *
 * @param {string|number} projectId
 * @param {{ previewUrl?: string|null, showBanner?: boolean }} [options]
 */
export function setProjectPublishFlash(projectId, options = {}) {
  if (!projectId || !canUseStorage()) {
    return;
  }

  const showBanner = options.showBanner !== false;
  const at = Date.now();
  const previewUrl =
    typeof options.previewUrl === 'string' && options.previewUrl
      ? options.previewUrl
      : null;
  const key = storageKey(projectId);

  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({
        previewUrl,
        showBanner,
        at,
      })
    );
  } catch (err) {
    // Quota often comes from a large data-URL preview — retry banner-only.
    if (previewUrl) {
      try {
        window.sessionStorage.setItem(
          key,
          JSON.stringify({
            previewUrl: null,
            showBanner,
            at,
          })
        );
      } catch (retryErr) {
        // Quota / private mode — ignore
      }
    }
  }
}

/**
 * Read flash without consuming the banner flag.
 *
 * @param {string|number} projectId
 * @returns {{ previewUrl: string|null, showBanner: boolean } | null}
 */
export function peekProjectPublishFlash(projectId) {
  if (!projectId || !canUseStorage()) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(storageKey(projectId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      previewUrl:
        typeof parsed.previewUrl === 'string' && parsed.previewUrl
          ? parsed.previewUrl
          : null,
      showBanner: Boolean(parsed.showBanner),
    };
  } catch (err) {
    return null;
  }
}

/**
 * Return whether the success banner should show, and clear that flag so it
 * never appears on a later visit. Preview URL is left intact for the hero.
 *
 * @param {string|number} projectId
 * @returns {boolean}
 */
export function consumeProjectPublishBanner(projectId) {
  const flash = peekProjectPublishFlash(projectId);
  if (!flash || !flash.showBanner) {
    return false;
  }

  try {
    window.sessionStorage.setItem(
      storageKey(projectId),
      JSON.stringify({
        previewUrl: flash.previewUrl,
        showBanner: false,
        at: Date.now(),
      })
    );
  } catch (err) {
    // ignore
  }

  return true;
}

/**
 * @param {string|number} projectId
 * @returns {string|null}
 */
export function getProjectPublishPreview(projectId) {
  const flash = peekProjectPublishFlash(projectId);
  return flash ? flash.previewUrl : null;
}

/**
 * @param {string|number} projectId
 */
export function clearProjectPublishFlash(projectId) {
  if (!projectId || !canUseStorage()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(storageKey(projectId));
  } catch (err) {
    // ignore
  }
}
